import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';


const formatCurrency = (val) => Number(val || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });

function AnalyticsDashboard() {
  const formatDateInput = (date) => {
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const location = useLocation();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [subscription, setSubscription] = useState({ subscription_status: 'free' });
  const [propertyCount, setPropertyCount] = useState(0);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [properties, setProperties] = useState([]);
  const [applications, setApplications] = useState([]);

  const [maintenance, setMaintenance] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [messageUnread, setMessageUnread] = useState(0);
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    address_line1: '',
    city: '',
    province: '',
    postal_code: '',
    country: '',
    date_of_birth: ''
  });
  const [profileStatus, setProfileStatus] = useState(null);
  const [profileError, setProfileError] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState(null);
  const [upgradingPlan, setUpgradingPlan] = useState(null);
  const [verification, setVerification] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(true);
  const [verificationError, setVerificationError] = useState(null);
  const [identityStatusMessage, setIdentityStatusMessage] = useState(null);

  const [filters, setFilters] = useState({
    property_id: ''
  });
  const [activeTab, setActiveTab] = useState('new-items');
  const [subscriptionBanner, setSubscriptionBanner] = useState(null);
  const [connectStatus, setConnectStatus] = useState({ connected: false });
  const [connectLoading, setConnectLoading] = useState(true); // eslint-disable-line no-unused-vars
  const [showNotificationsBanner, setShowNotificationsBanner] = useState(() => {
    return localStorage.getItem('rentual_notifications_banner_dismissed') !== 'true';
  });

  const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

  const loadStripeJs = () => {
    if (window.Stripe) return Promise.resolve(window.Stripe);
    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[src="https://js.stripe.com/v3/"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(window.Stripe));
        existing.addEventListener('error', reject);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => resolve(window.Stripe);
      script.onerror = () => reject(new Error('Failed to load Stripe.js'));
      document.body.appendChild(script);
    });
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString();
  };

  const validatePostal = (value, countryValue) => {
    if (!countryValue || !value) return true;
    const normalizedCountry = countryValue.trim().toLowerCase();
    if (normalizedCountry === 'canada' || normalizedCountry === 'ca') {
      return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(value.trim());
    }
    if (normalizedCountry === 'united states' || normalizedCountry === 'usa' || normalizedCountry === 'us') {
      return /^\d{5}(-\d{4})?$/.test(value.trim());
    }
    return true;
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const saveProfile = async () => {
    setProfileError(null);
    setProfileStatus(null);
    if (!validatePostal(profile.postal_code, profile.country)) {
      setProfileError('Please enter a valid postal/ZIP code for the selected country.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    setProfileSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(profile)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to update profile');
      setProfile(data.profile || profile);
      setProfileStatus('Profile saved.');
    } catch (err) {
      setProfileError(err.message || 'Unable to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleManageBilling = async () => {
    setBillingError(null);
    const token = localStorage.getItem('token');
    if (!token) return;
    setBillingLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/subscription/portal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to open billing portal');
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Billing portal unavailable.');
      }
    } catch (err) {
      setBillingError(err.message || 'Unable to open billing portal');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleConnect = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/connect/onboard`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to start onboarding.');
      if (data.url) window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'Unable to start onboarding.');
    }
  };

  const [checkoutPromo, setCheckoutPromo] = useState('');
  const [checkoutPromoValid, setCheckoutPromoValid] = useState(null); // null | { code, discount_type, discount_value, description } | 'invalid'
  const [checkoutPromoLoading, setCheckoutPromoLoading] = useState(false);

  const validateCheckoutPromo = async () => {
    if (!checkoutPromo.trim()) return;
    const token = localStorage.getItem('token');
    setCheckoutPromoLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/promo/validate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: checkoutPromo.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.promo) {
        setCheckoutPromoValid(data.promo);
      } else {
        setCheckoutPromoValid('invalid');
      }
    } catch {
      setCheckoutPromoValid('invalid');
    } finally {
      setCheckoutPromoLoading(false);
    }
  };

  const handleUpgrade = async (plan) => {
    setBillingError(null);
    const token = localStorage.getItem('token');
    if (!token) return;
    setUpgradingPlan(plan);
    try {
      const body = { plan };
      // Include validated promo code for percent/amount discounts
      if (checkoutPromoValid && checkoutPromoValid !== 'invalid' && (checkoutPromoValid.discount_type === 'percent' || checkoutPromoValid.discount_type === 'amount')) {
        body.promo_code = checkoutPromoValid.code;
      }
      // For full_access/free_months, apply directly (no Stripe discount needed)
      if (checkoutPromoValid && checkoutPromoValid !== 'invalid' && (checkoutPromoValid.discount_type === 'full_access' || checkoutPromoValid.discount_type === 'free_months')) {
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/promo/apply`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: checkoutPromoValid.code })
        });
        window.location.reload();
        return;
      }
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/subscription/upgrade`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to start checkout');
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Checkout unavailable.');
      }
    } catch (err) {
      setBillingError(err.message || 'Unable to start checkout');
    } finally {
      setUpgradingPlan(null);
    }
  };
  const getVerificationBadge = () => {
    if (!verification) return { label: 'Unverified', tone: 'warning' };
    if (verification.ownership_status === 'approved') return { label: 'Verified landlord', tone: 'paid' };
    if (verification.id_status === 'verified' || verification.id_status === 'id_verified') return { label: 'ID verified', tone: 'muted' };
    if (verification.id_status === 'pending') return { label: 'ID in review', tone: 'pending' };
    if (verification.id_status === 'mismatch') return { label: 'Verification mismatch', tone: 'warning' };
    if (verification.id_status === 'failed') return { label: 'ID failed', tone: 'warning' };
    return { label: 'Unverified', tone: 'warning' };
  };

  const handleStartIdentity = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setVerificationError(null);
    setIdentityStatusMessage(null);
    try {
      if (!STRIPE_PUBLISHABLE_KEY) {
        throw new Error('Stripe publishable key not configured.');
      }
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/verification/identity/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Unable to start identity verification');
      if (!data.client_secret) throw new Error('Missing Stripe client secret.');

      const stripeLoader = await loadStripeJs();
      const stripe = stripeLoader(STRIPE_PUBLISHABLE_KEY);
      const { error: stripeError } = await stripe.verifyIdentity(data.client_secret);

      if (stripeError) {
        // User closed or cancelled the modal - don't lock them out
        // Reset status so they can retry
        setVerification((prev) => prev ? { ...prev, id_status: 'unverified' } : prev);
        throw new Error(stripeError.message || 'Verification was cancelled. You can try again.');
      }
      setVerification((prev) => prev ? { ...prev, id_status: 'pending' } : prev);
      setIdentityStatusMessage('Identity verification submitted. Stripe will notify us once it is reviewed.');
    } catch (err) {
      setVerificationError(err.message || 'Unable to start identity verification');
    }
  };

  const buildEventsQuery = useCallback(() => {
    const params = new URLSearchParams();
    params.set('event_name', 'property_view');
    if (filters.property_id) params.set('property_id', filters.property_id);
    params.set('limit', '1000');
    params.set('offset', '0');
    return params.toString();
  }, [filters.property_id]);

  const fetchEvents = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setEventsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/analytics/events?${buildEventsQuery()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to fetch events');
      setEvents(data.events || []);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setEventsLoading(false);
    }
  }, [buildEventsQuery]);

  useEffect(() => {
    const fetchSummary = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      const params = new URLSearchParams(location.search);
      const successParam = params.get('success') === 'true';
      const canceledParam = params.get('canceled') === 'true';
      const sessionId = params.get('session_id');
      if ((successParam || canceledParam) && activeTab !== 'subscription') {
        setActiveTab('subscription');
      }
      if (successParam) {
        setSubscriptionBanner({ type: 'success', message: 'Subscription processed successfully. We are confirming your upgrade now.' });
        if (sessionId) {
          try {
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/subscription/confirm`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ sessionId })
            });
          } catch (err) {
            setSubscriptionBanner({ type: 'error', message: 'We could not confirm the subscription yet. Please refresh or contact support.' });
          }
        }
      }
      if (canceledParam) {
        setSubscriptionBanner({ type: 'canceled', message: 'Checkout was canceled. No payment was taken.' });
      }
      try {
        const [res, propertiesRes, applicationsRes, maintenanceRes, tenantsRes, messagesRes, profileRes, verificationRes, subscriptionRes, connectRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/analytics/summary`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/properties`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/maintenance`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/active-tenants`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/messages/unread-count`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/verification/status`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/subscription`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/connect/status`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to fetch analytics');
        setSummary(data);
        const propertiesData = propertiesRes.ok ? await propertiesRes.json().catch(() => ({})) : { properties: [] };
        const applicationsData = applicationsRes.ok ? await applicationsRes.json().catch(() => ({})) : { applications: [] };
        const maintenanceData = maintenanceRes.ok ? await maintenanceRes.json().catch(() => ({})) : { requests: [] };
        const tenantsData = tenantsRes.ok ? await tenantsRes.json().catch(() => ([])) : [];
        const messagesData = messagesRes.ok ? await messagesRes.json().catch(() => ({})) : {};
        const profileData = profileRes.ok ? await profileRes.json().catch(() => ({})) : {};
        if (profileRes.ok && profileData.profile) {
          setProfile(profileData.profile);
        }
        const verificationData = verificationRes.ok ? await verificationRes.json().catch(() => ({})) : {};
        if (verificationRes.ok) {
          setVerification(verificationData.verification || null);
        } else {
          setVerificationError('Unable to load verification status.');
        }
        const subscriptionData = subscriptionRes.ok ? await subscriptionRes.json().catch(() => ({})) : { subscription: { subscription_status: 'free' }, propertyCount: 0 };
        setSubscription(subscriptionData.subscription || { subscription_status: 'free' });
        setPropertyCount(subscriptionData.propertyCount || 0);
        setProperties(propertiesData.properties || []);
        setApplications(applicationsData.applications || []);
        setMaintenance(maintenanceData.requests || []);
        setTenants(Array.isArray(tenantsData) ? tenantsData : []);
        setMessageUnread(messagesData.count || 0);
        const connectData = connectRes.ok ? await connectRes.json().catch(() => ({})) : { connected: false };
        setConnectStatus(connectData);
      } catch (err) {
        setError(err.message || 'Something went wrong');
      } finally {
        setLoading(false);
        setVerificationLoading(false);
        setConnectLoading(false);
      }
    };

    fetchSummary();
    if (activeTab === 'portfolio') {
      fetchEvents();
    }
  }, [activeTab, fetchEvents, location.search]);

  useEffect(() => {
    if (activeTab === 'portfolio') {
      fetchEvents();
    }
  }, [activeTab, filters.property_id, fetchEvents]);

  useEffect(() => {
    const hashTab = location.hash?.replace('#', '');
    if (['new-items', 'portfolio', 'viewings', 'subscription', 'profile', 'payments', 'verification'].includes(hashTab)) {
      setActiveTab(hashTab);
    }
  }, [location.hash]);

  if (loading) {
    return (
      <main className="App-main">
        <div className="loading-card">
          <div className="loading-title">Loading owner dashboard…</div>
          <div className="loading-sub">Fetching your latest portfolio data.</div>
        </div>
      </main>
    );
  }
  if (error) return <main className="App-main"><p className="error-message">{error}</p></main>;
  if (!summary) return <main className="App-main">No owner dashboard data.</main>;

  const propertyViewEvents = events.filter((evt) => evt.event_name === 'property_view');
  const filteredPropertyViews = filters.property_id
    ? propertyViewEvents.filter((evt) => String(evt.property_id) === String(filters.property_id))
    : propertyViewEvents;

  const buildTrend = (days, offsetDays = 0) => {
    const dates = Array.from({ length: days }, (_, idx) => {
      const d = new Date();
      d.setDate(d.getDate() - offsetDays - (days - 1 - idx));
      return formatDateInput(d);
    });
    const counts = dates.map(date =>
      filteredPropertyViews.filter(evt => formatDateInput(evt.created_at) === date).length
    );
    const maxCount = Math.max(1, ...counts);
    const points = counts.map((count, idx) => {
      const x = (idx / (days - 1)) * 100;
      const y = 100 - (count / maxCount) * 100;
      return `${x},${y}`;
    }).join(' ');
    return {
      dates,
      counts,
      total: counts.reduce((sum, value) => sum + value, 0),
      points
    };
  };

  const buildMonthlyTrend = (months = 12) => {
    const monthKeys = [];
    const labels = [];
    for (let i = months - 1; i >= 0; i -= 1) {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthKeys.push(key);
      labels.push(d.toLocaleString('en-US', { month: 'short' }));
    }
    const counts = monthKeys.map((key) => {
      return filteredPropertyViews.filter((evt) => {
        const date = new Date(evt.created_at);
        const evtKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        return evtKey === key;
      }).length;
    });
    const maxCount = Math.max(1, ...counts);
    const points = counts.map((count, idx) => {
      const x = (idx / (months - 1)) * 100;
      const y = 100 - (count / maxCount) * 100;
      return `${x},${y}`;
    }).join(' ');
    return {
      labels,
      counts,
      total: counts.reduce((sum, value) => sum + value, 0),
      points
    };
  };

  const trend7 = buildTrend(7);
  const trend30 = buildTrend(30);
  const trendYear = buildMonthlyTrend(12);
  const trend7Prev = buildTrend(7, 7);
  const trend30Prev = buildTrend(30, 30);
  const allTimeViews = filteredPropertyViews.length;

  const calcGrowth = (current, previous) => {
    if (!previous) return current === 0 ? null : 100;
    return Math.round(((current - previous) / previous) * 100);
  };

  const growth7 = calcGrowth(trend7.total, trend7Prev.total);
  const growth30 = calcGrowth(trend30.total, trend30Prev.total);

  const formatGrowth = (value) => {
    if (value === null || Number.isNaN(value)) return '-';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value}%`;
  };

  const growthTone = (value) => {
    if (value === null || Number.isNaN(value)) return 'report-chip--muted';
    return value >= 0 ? 'report-chip--success' : 'report-chip--danger';
  };

  const TrendChart = ({ trend, stroke, gradientId }) => {
    const areaPoints = `${trend.points} 100,100 0,100`;
    return (
      <svg viewBox="0 0 100 100" width="100%" height="90" preserveAspectRatio="none">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.3" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={areaPoints} fill={`url(#${gradientId})`} />
        <polyline
          fill="none"
          stroke={stroke}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={trend.points}
        />
      </svg>
    );
  };

  const newApplicationsCount = applications.filter((app) => app.status === 'submitted').length;
  const openMaintenanceCount = maintenance.filter((req) => req.status !== 'resolved').length;
  const newTenantsCount = tenants.length;
  const updatedPropertiesCount = properties.filter((property) => property.updated_at && property.created_at && property.updated_at !== property.created_at).length;

  const dashboardSummaryItems = [
    { id: 'viewings', label: 'Upcoming viewings', count: 0, link: '/landlord/viewings' },
    { id: 'applications', label: 'New applications', count: newApplicationsCount, link: '/landlord/applicants' },
    { id: 'maintenance', label: 'Open maintenance', count: openMaintenanceCount, link: '/landlord/maintenance' },
    { id: 'tenants', label: 'Active tenants', count: newTenantsCount, link: '/landlord/my-tenants' },
    { id: 'properties', label: 'Updated properties', count: updatedPropertiesCount, link: '/landlord/dashboard' },
    { id: 'messages', label: 'Unread messages', count: messageUnread, link: '/landlord/messages' }
  ];

  const totalApplications = summary?.funnel?.reduce((sum, row) => sum + (row.count || 0), 0) || 0;
  const approvedCount = summary?.funnel?.find((row) => row.status === 'approved')?.count || 0;
  const approvalRate = totalApplications === 0 ? 0 : Math.round((approvedCount / totalApplications) * 100);
  const maintenanceOpen = summary?.maintenance?.reduce((sum, row) => sum + (row.status !== 'resolved' ? (row.count || 0) : 0), 0) || 0;
  const rentTotals = (summary?.rent || []).reduce((acc, row) => {
    acc[row.status] = Number(row.total) || 0;
    return acc;
  }, {});
  const rentOutstanding = Object.entries(rentTotals).reduce((sum, [status, total]) => {
    return status === 'paid' ? sum : sum + total;
  }, 0);

  const funnelItems = (summary?.funnel || []).map((row) => ({
    label: row.status?.replace(/_/g, ' ') || 'unknown',
    value: row.count || 0
  }));
  const maintenanceItems = (summary?.maintenance || []).map((row) => ({
    label: row.status?.replace(/_/g, ' ') || 'unknown',
    value: row.count || 0
  }));
  const rentItems = (summary?.rent || []).map((row) => ({
    label: row.status?.replace(/_/g, ' ') || 'unknown',
    value: Number(row.total) || 0
  }));

  const mismatchItems = Array.isArray(verification?.mismatches) ? verification.mismatches : [];
  const hasMismatch = verification?.id_status === 'mismatch' && mismatchItems.length > 0;

  const handleDismissNotifications = () => {
    localStorage.setItem('rentual_notifications_banner_dismissed', 'true');
    setShowNotificationsBanner(false);
  };

  const formatMismatchField = (field) => {
    const map = {
      first_name: 'First name',
      last_name: 'Last name',
      dob: 'Date of birth',
      address_line1: 'Address line 1',
      city: 'City',
      province: 'Province/State',
      postal_code: 'Postal code',
      country: 'Country'
    };
    return map[field] || field;
  };

  const renderNoticeStack = () => {
    // 0. Notifications (Contextual)
    const totalNewItems = newApplicationsCount + openMaintenanceCount + messageUnread;
    if (showNotificationsBanner && totalNewItems > 0) {
      return (
        <div className="dashboard-section" style={{ borderLeft: '4px solid #2f6fed', background: '#f5f8ff', padding: '0.75rem', marginBottom: '1rem', position: 'relative' }}>
          <button
            type="button"
            onClick={handleDismissNotifications}
            style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#666' }}
            aria-label="Dismiss"
          >
            &times;
          </button>
          <div className="section-header" style={{ alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Portfolio Updates</h3>
            <span className="report-chip report-chip--info">{totalNewItems} items</span>
          </div>
          <p className="status-text" style={{ marginTop: '0.35rem' }}>
            You have {totalNewItems} new item{totalNewItems > 1 ? 's' : ''} requiring attention, including {messageUnread} unread message{messageUnread !== 1 ? 's' : ''}.
          </p>
          <div className="form-actions" style={{ marginTop: '0.5rem' }}>
            <button className="secondary-button" onClick={() => setActiveTab('new-items')}>View all updates</button>
            <Link to="/landlord/messages" className="secondary-button">Go to Messages</Link>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderOnboardingChecklist = () => {
    const idDone = verification?.id_status === 'verified' || verification?.id_status === 'id_verified';
    const idPending = verification?.id_status === 'pending';
    const subDone = subscription?.subscription_status === 'active';
    const hasProperties = propertyCount > 0;
    const connectDone = connectStatus.connected;
    const allDone = idDone && subDone && hasProperties;

    if (allDone && idDone) return null;

    const steps = [
      {
        key: 'verify',
        label: 'Verify your identity',
        description: idPending ? 'Verification in progress -- usually takes under a minute.' : hasMismatch ? 'Profile details didn\'t match your ID. Update your profile and retry.' : 'Quick ID check to build tenant trust.',
        done: idDone,
        pending: idPending,
        action: idPending ? null : hasMismatch ? (
          <div className="form-actions" style={{ gap: '0.5rem' }}>
            <button className="secondary-button" onClick={() => setActiveTab('profile')}>Review profile</button>
            <button className="secondary-button" onClick={handleStartIdentity}>Retry</button>
          </div>
        ) : (
          <button className="secondary-button" onClick={handleStartIdentity} disabled={!STRIPE_PUBLISHABLE_KEY}>Verify now</button>
        )
      },
      {
        key: 'subscribe',
        label: 'Choose a plan',
        description: 'Unlock listings, rent collection, and tenant screening.',
        done: subDone,
        action: subDone ? null : (
          <button className="secondary-button" onClick={() => handleUpgrade('monthly')} disabled={upgradingPlan !== null}>
            {upgradingPlan === 'monthly' ? 'Starting...' : 'Subscribe'}
          </button>
        )
      },
      {
        key: 'property',
        label: 'Add your first property',
        description: 'List a property so tenants can find it.',
        done: hasProperties,
        action: hasProperties ? null : (
          <button className="secondary-button" onClick={() => navigate('/landlord/properties/new')}>Add property</button>
        )
      },
      ...(subDone && !connectDone ? [{
        key: 'connect',
        label: 'Connect your bank account',
        description: 'Accept rent payments directly to your account.',
        done: connectDone,
        action: <button className="secondary-button" onClick={handleConnect}>Connect Stripe</button>
      }] : [])
    ];

    return (
      <section className="dossier-section report-section" style={{ borderLeft: '4px solid var(--accent-color, #28b6c8)', marginBottom: '1rem' }}>
        <div className="section-header" style={{ alignItems: 'center' }}>
          <h3>Get started</h3>
          <span className="report-chip report-chip--muted">{steps.filter(s => s.done).length}/{steps.length} complete</span>
        </div>
        <div style={{ marginTop: '0.75rem' }}>
          {steps.map((step) => (
            <div key={step.key} style={{
              display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
              padding: '0.6rem 0', borderBottom: '1px solid var(--border-color, #e5e7eb)',
              opacity: step.done ? 0.6 : 1
            }}>
              <span style={{ fontSize: '1.1rem', marginTop: '0.1rem' }}>
                {step.done ? '\u2705' : step.pending ? '\u23F3' : '\u25CB'}
              </span>
              <div style={{ flex: 1 }}>
                <strong style={{ textDecoration: step.done ? 'line-through' : 'none' }}>{step.label}</strong>
                {!step.done && <p className="status-text" style={{ margin: '0.2rem 0 0.4rem' }}>{step.description}</p>}
                {!step.done && step.action}
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  };

  return (
    <main className="App-main report-page">
      {renderNoticeStack()}

      <div className="subnav-wrap">
        <nav className="analytics-subnav" aria-label="Owner dashboard sections">
          <button type="button" className={activeTab === 'new-items' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('new-items')}>New items</button>
          <button type="button" className={activeTab === 'portfolio' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('portfolio')}>Portfolio</button>
          <button type="button" className={activeTab === 'viewings' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('viewings')}>Viewings</button>
          <button type="button" className={activeTab === 'subscription' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('subscription')}>Subscription</button>
          <button type="button" className={activeTab === 'profile' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('profile')}>Profile</button>
          <button type="button" className={activeTab === 'payments' ? 'primary-button' : 'secondary-button'} onClick={() => navigate('/landlord/settings')}>Payments</button>
          <button type="button" className={activeTab === 'verification' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('verification')}>Verification</button>
        </nav>
      </div>

      {activeTab === 'new-items' && renderOnboardingChecklist()}

      {activeTab === 'new-items' && (
      <section id="analytics-new-items" className="dossier-section report-section new-items-card" style={{ borderLeft: '4px solid #2f6fed', background: '#f5f8ff', marginBottom: '1rem' }}>
        <div className="section-header">
          <h3>New items</h3>
          <span className="report-chip report-chip--muted">Portfolio activity</span>
        </div>
        <div className="kpi-grid" style={{ marginTop: '0.5rem' }}>
          {dashboardSummaryItems.map((item) => (
            <div key={item.id} className={`kpi-card ${item.count === 0 ? 'muted' : ''}`}>
              <span>{item.label}</span>
              <strong>{item.count}</strong>
              <div className="form-actions" style={{ marginTop: '0.35rem' }}>
                <a className="secondary-button" href={item.link}>View</a>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      {activeTab === 'viewings' && (
      <section id="viewings" className="dashboard-section" style={{ marginBottom: '1rem' }}>
        <div className="section-header">
          <h3>Viewing Calendar</h3>
          <span className="report-chip report-chip--muted">Upcoming</span>
        </div>
        <p className="status-text">View and manage upcoming viewings from the Viewings page.</p>
        <div className="form-actions" style={{ marginTop: '0.75rem' }}>
          <Link className="secondary-button" to="/landlord/viewings">Manage viewings</Link>
        </div>
      </section>
      )}

      {activeTab === 'subscription' && (
      <section id="analytics-subscription" className="dashboard-section" style={{ marginBottom: '1rem' }}>
        {subscriptionBanner && (
          <div className={`dashboard-section new-items-card ${subscriptionBanner.type === 'success' ? '' : 'muted'}`} style={{ borderLeft: subscriptionBanner.type === 'success' ? '4px solid #2f6fed' : '4px solid #d93025', background: subscriptionBanner.type === 'success' ? '#f5f8ff' : '#fff5f5', marginBottom: '1rem' }}>
            <div className="section-header">
              <h3>{subscriptionBanner.type === 'success' ? 'Upgrade successful' : 'Checkout canceled'}</h3>
              <span className={`report-chip ${subscriptionBanner.type === 'success' ? 'report-chip--success' : 'report-chip--muted'}`}>
                {subscriptionBanner.type === 'success' ? 'Confirmed' : 'Not charged'}
              </span>
            </div>
            <p className="status-text">{subscriptionBanner.message}</p>
          </div>
        )}
        <div className={`dashboard-section report-section new-items-card ${subscription?.subscription_status === 'active' ? '' : 'muted'}`} style={{ borderLeft: '4px solid #2f6fed', background: '#f5f8ff', marginBottom: '1rem' }}>
          <div className="section-header" style={{ alignItems: 'center' }}>
            <h3>{subscription?.subscription_status === 'active' ? 'Subscription active' : 'Free plan'}</h3>
            <span className={`report-chip ${subscription?.subscription_status === 'active' ? 'report-chip--success' : 'report-chip--muted'}`}>
              {subscription?.subscription_status === 'active' ? 'Active' : 'Free'}
            </span>
          </div>
          <p className="status-text">
            {subscription?.subscription_status === 'active'
              ? 'Your Pro plan is active. Manage billing anytime from here.'
              : 'Upgrade when you\'re ready to publish more listings and unlock Pro features.'}
          </p>
        </div>

        <div className="ledger-grid" style={{ marginTop: '0.75rem' }}>
          <div className="ledger-item">
            <div className="section-header" style={{ alignItems: 'center' }}>
              <h3>Plan details</h3>
              <span className={`report-chip ${subscription?.subscription_status === 'active' ? 'report-chip--success' : 'report-chip--muted'}`}>
                {subscription?.subscription_status === 'active' ? 'Active' : 'Free'}
              </span>
            </div>
            <p className="status-text"><strong>Plan:</strong> {subscription?.subscription_plan && subscription.subscription_plan !== 'admin'
              ? subscription.subscription_plan
              : (subscription?.subscription_status === 'active' ? 'Pro' : 'Free')}</p>
            <p className="status-text"><strong>Renews:</strong> {formatDate(subscription.subscription_ends_at)}</p>
            {subscription?.subscription_status !== 'active' && (
              <p className="status-text"><strong>Limit:</strong> 1 active property · Current properties: {propertyCount || 0}</p>
            )}
            {billingError && <p className="error-message">{billingError}</p>}
            <div className="form-actions" style={{ marginTop: '0.5rem', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              {subscription?.subscription_status === 'active' ? (
                <button className="secondary-button" type="button" onClick={handleManageBilling} disabled={billingLoading}>
                  {billingLoading ? 'Opening billing…' : 'Manage subscription'}
                </button>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <input
                      className="form-control"
                      placeholder="Promo code (optional)"
                      value={checkoutPromo}
                      onChange={(e) => { setCheckoutPromo(e.target.value); setCheckoutPromoValid(null); }}
                      style={{ maxWidth: '180px' }}
                    />
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={validateCheckoutPromo}
                      disabled={!checkoutPromo.trim() || checkoutPromoLoading}
                    >
                      {checkoutPromoLoading ? 'Checking…' : 'Apply'}
                    </button>
                  </div>
                  {checkoutPromoValid && checkoutPromoValid !== 'invalid' && (
                    <p className="status-text" style={{ color: 'var(--accent-color)', marginTop: '0.25rem' }}>
                      {checkoutPromoValid.discount_type === 'percent' ? `${checkoutPromoValid.discount_value}% off` :
                       checkoutPromoValid.discount_type === 'amount' ? `$${checkoutPromoValid.discount_value} off` :
                       checkoutPromoValid.discount_type === 'free_months' ? `${checkoutPromoValid.duration_months} month${checkoutPromoValid.duration_months > 1 ? 's' : ''} free` :
                       'Full access granted'} {checkoutPromoValid.description ? `- ${checkoutPromoValid.description}` : ''}
                    </p>
                  )}
                  {checkoutPromoValid === 'invalid' && (
                    <p className="error-message" style={{ marginTop: '0.25rem' }}>Invalid or expired promo code.</p>
                  )}
                  <button
                    className="primary-button"
                    type="button"
                    onClick={() => handleUpgrade('monthly')}
                    disabled={upgradingPlan !== null}
                  >
                    {upgradingPlan === 'monthly' ? 'Starting checkout…' : 'Subscribe to Pro'}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="ledger-item">
            <div className="section-header">
              <h3>Pro includes</h3>
              <span className="report-chip report-chip--muted">Highlights</span>
            </div>
            <ul className="status-text" style={{ marginTop: '0.5rem', paddingLeft: '1.25rem' }}>
              <li>Unlimited listings + publishing controls</li>
              <li>Applicant screening workflow + dossier</li>
              <li>Maintenance and tenant communication hub</li>
              <li>Rent ledger visibility + reminders</li>
            </ul>
            {subscription?.subscription_status !== 'active' && (
              <p className="status-text" style={{ marginTop: '0.5rem' }}>
                Need a tailored plan? <Link to="/contact">Talk to us</Link>.
              </p>
            )}
          </div>
        </div>
      </section>
      )}

      {activeTab === 'profile' && (
      <section id="analytics-profile" className="dashboard-section" style={{ marginBottom: '1rem' }}>
        <div className="section-header">
          <h3>Profile</h3>
          <span className="report-chip report-chip--muted">Used for verification</span>
        </div>
        {profileError && <p className="error-message">{profileError}</p>}
        {profileStatus && <p className="status-text">{profileStatus}</p>}
        <div className="ledger-grid" style={{ marginTop: '0.75rem' }}>
          <div className="ledger-item">
            <div className="form-group">
              <label>First name</label>
              <input name="first_name" className="form-control" value={profile.first_name || ''} onChange={handleProfileChange} />
            </div>
            <div className="form-group">
              <label>Last name</label>
              <input name="last_name" className="form-control" value={profile.last_name || ''} onChange={handleProfileChange} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input name="phone" className="form-control" value={profile.phone || ''} onChange={handleProfileChange} />
            </div>
            <div className="form-group">
              <label>Date of birth</label>
              <input name="date_of_birth" type="date" className="form-control" value={(profile.date_of_birth || '').slice(0,10)} onChange={handleProfileChange} />
            </div>
          </div>
          <div className="ledger-item">
            <div className="form-group">
              <label>Address</label>
              <input name="address_line1" className="form-control" value={profile.address_line1 || ''} onChange={handleProfileChange} />
            </div>
            <div className="form-group">
              <label>City</label>
              <input name="city" className="form-control" value={profile.city || ''} onChange={handleProfileChange} />
            </div>
            <div className="form-group">
              <label>Province/State</label>
              <input name="province" className="form-control" value={profile.province || ''} onChange={handleProfileChange} />
            </div>
            <div className="form-group">
              <label>Postal code</label>
              <input name="postal_code" className="form-control" value={profile.postal_code || ''} onChange={handleProfileChange} />
            </div>
            <div className="form-group">
              <label>Country</label>
              <select name="country" className="form-control" value={profile.country || 'Canada'} onChange={handleProfileChange}>
                <option value="Canada">Canada</option>
              </select>
            </div>
          </div>
        </div>
        <button className="secondary-button" onClick={saveProfile} disabled={profileSaving}>
          {profileSaving ? 'Saving�' : 'Save profile'}
        </button>
      </section>
      )}

      {activeTab === 'verification' && (
      <section id="analytics-verification" className="dashboard-section report-section" style={{ marginBottom: '1rem' }}>
        <div className="section-header" style={{ alignItems: 'center' }}>
          <h3>Landlord verification</h3>
          <Link to="/verification" className={`status-pill ${getVerificationBadge().tone}`} title="Learn what landlord verification means">
            {getVerificationBadge().label}
          </Link>
        </div>
        <p className="status-text" style={{ marginTop: '0.25rem' }}>Complete these steps to publish listings and build tenant trust.</p>
        {verificationLoading && <p className="status-text">Loading verification status�</p>}
        {!verificationLoading && (
          <>
            {verificationError && <p className="error-message">{verificationError}</p>}
            {verification && (
              <div className="ledger-grid" style={{ marginBottom: '1rem' }}>
                <div className="ledger-item">
                  <div className="ledger-item-header">
                    <h3>Identity check</h3>
                    <Link to="/verification" className={`status-pill ${verification.id_status || 'muted'}`} title="Learn what landlord verification means">
                      {verification.id_status || 'unverified'}
                    </Link>
                  </div>
                  <p className="status-text">Secure ID verification through Stripe Identity.</p>
                  {identityStatusMessage && <p className="status-text">{identityStatusMessage}</p>}
                  {verification.id_status === 'mismatch' && mismatchItems.length > 0 && (
                    <div className="dashboard-section verification-mismatch-banner" style={{ marginTop: '0.5rem', borderLeft: '4px solid #d93025', padding: '0.5rem' }}>
                      <p className="status-text" style={{ margin: 0 }}>
                        Verification mismatch. Please update your profile to match these fields and retry.
                      </p>
                      <ul className="status-text" style={{ marginTop: '0.35rem', marginBottom: 0, paddingLeft: '1.25rem' }}>
                        {mismatchItems.map((item, idx) => (
                          <li key={`${item.field_name}-${idx}`}>{formatMismatchField(item.field_name)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <button
                    className="secondary-button"
                    onClick={handleStartIdentity}
                    disabled={!STRIPE_PUBLISHABLE_KEY || verification.id_status === 'verified'}
                    style={{ marginTop: '0.5rem' }}
                  >
                    {verification.id_status === 'verified'
                      ? 'Identity verified'
                      : verification.id_status === 'pending'
                        ? 'Resume verification'
                        : verification.id_status === 'mismatch'
                          ? 'Retry verification'
                          : 'Verify identity'}
                  </button>
                  {!STRIPE_PUBLISHABLE_KEY && (
                    <p className="status-text" style={{ marginTop: '0.5rem' }}>Stripe Identity is not configured yet.</p>
                  )}
                </div>
              </div>
            )}
            <p className="status-text">Never request or share SIN/bank details. Verification protects tenants and helps you publish listings faster.</p>
          </>
        )}
      </section>
      )}

      {activeTab === 'portfolio' && (
        <>
          <section id="analytics-portfolio" className="dossier-section report-section">
            <div className="section-header">
              <h3>Portfolio Overview</h3>
              <span className="report-chip report-chip--info">Snapshot</span>
            </div>
            <div className="kpi-grid" style={{ marginTop: '0.75rem' }}>
              <div className="kpi-card">
                <span>Total Properties</span>
                <strong>{summary.totalProperties}</strong>
              </div>
              <div className="kpi-card">
                <span>Active Leases</span>
                <strong>{summary.activeLeases}</strong>
              </div>
              <div className="kpi-card">
                <span>Occupancy Rate</span>
                <strong>{summary.occupancyRate}%</strong>
              </div>
              <div className="kpi-card">
                <span>Applications</span>
                <strong>{totalApplications}</strong>
              </div>
              <div className="kpi-card">
                <span>Approval Rate</span>
                <strong>{approvalRate}%</strong>
              </div>
              <div className="kpi-card">
                <span>Open Maintenance</span>
                <strong>{maintenanceOpen}</strong>
              </div>
              <div className="kpi-card">
                <span>Rent Outstanding</span>
                <strong>{formatCurrency(rentOutstanding)}</strong>
              </div>
            </div>
          </section>

          <section id="analytics-operations" className="dossier-section report-section">
            <div className="section-header">
              <h3>Operational Health</h3>
              <span className="report-chip report-chip--success">Live</span>
            </div>
            <div className="ledger-grid" style={{ marginTop: '0.75rem' }}>
              <div className="ledger-item" style={{ gridColumn: '1 / -1' }}>
                <h3>Application Funnel</h3>
                <div className="bar-list">
                  {funnelItems.length === 0 && <p className="status-text">No applications yet.</p>}
                  {funnelItems.map((item) => (
                    <div key={item.label} className="bar-row">
                      <span>{item.label}</span>
                      <div className="bar-track">
                        <div className="bar-fill" style={{ width: `${totalApplications ? (item.value / totalApplications) * 100 : 0}%` }}></div>
                      </div>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
              <div className="ledger-item">
                <h3>Maintenance</h3>
                <div className="bar-list">
                  {maintenanceItems.length === 0 && <p className="status-text">No maintenance activity.</p>}
                  {maintenanceItems.map((item) => (
                    <div key={item.label} className="bar-row">
                      <span>{item.label}</span>
                      <div className="bar-track">
                        <div className="bar-fill" style={{ width: `${maintenanceOpen ? (item.value / Math.max(maintenanceOpen, 1)) * 100 : 0}%` }}></div>
                      </div>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
              <div className="ledger-item">
                <h3>Rent Health</h3>
                <div className="bar-list">
                  {rentItems.length === 0 && <p className="status-text">No rent ledger yet.</p>}
                  {rentItems.map((item) => (
                    <div key={item.label} className="bar-row">
                      <span>{item.label}</span>
                      <div className="bar-track">
                        <div className="bar-fill" style={{ width: `${rentOutstanding ? (item.value / Math.max(rentOutstanding, 1)) * 100 : 0}%` }}></div>
                      </div>
                      <strong>${Math.round(item.value).toLocaleString()}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section id="analytics-activity" className="dossier-section report-section">
            <div className="section-header">
              <h3>Analytics & Activity</h3>
              <span className="report-chip report-chip--info">Property views</span>
            </div>
            <p className="status-text" style={{ marginTop: '0.5rem' }}>
              Property view trends across your portfolio. Filter by property for a focused report.
            </p>
            <div className="maintenance-form report-filters" style={{ marginTop: '0.75rem', marginBottom: '1rem' }}>
              <select
                value={filters.property_id}
                onChange={(e) => setFilters(prev => ({ ...prev, property_id: e.target.value }))}
              >
                <option value="">All properties</option>
                {properties.map((property) => (
                  <option key={property.id} value={property.id}>{property.address}</option>
                ))}
              </select>
            </div>

            {eventsLoading ? (
              <p className="status-text">Loading property views...</p>
            ) : (
              <>
                <div className="kpi-grid" style={{ marginBottom: '1rem' }}>
                  <div className="kpi-card">
                    <span>Last 7 days</span>
                    <strong>{trend7.total}</strong>
                    <p className="status-text">Property views</p>
                    <span className={`report-chip ${growthTone(growth7)}`}>{formatGrowth(growth7)}</span>
                  </div>
                  <div className="kpi-card">
                    <span>Last 30 days</span>
                    <strong>{trend30.total}</strong>
                    <p className="status-text">Property views</p>
                    <span className={`report-chip ${growthTone(growth30)}`}>{formatGrowth(growth30)}</span>
                  </div>
                  <div className="kpi-card">
                    <span>All time</span>
                    <strong>{allTimeViews}</strong>
                    <p className="status-text">Property views</p>
                  </div>
                </div>

                <div className="ledger-grid">
                  <div className="ledger-item">
                    <h3>7-day trend</h3>
                    {trend7.total === 0 ? (
                      <p className="status-text">No views yet.</p>
                    ) : (
                      <>
                        <TrendChart trend={trend7} stroke="#4f46e5" gradientId="trend-7" />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', opacity: 0.7 }}>
                          <span>{trend7.dates[0]}</span>
                          <span>{trend7.dates[trend7.dates.length - 1]}</span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="ledger-item">
                    <h3>30-day trend</h3>
                    {trend30.total === 0 ? (
                      <p className="status-text">No views yet.</p>
                    ) : (
                      <>
                        <TrendChart trend={trend30} stroke="#0f766e" gradientId="trend-30" />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', opacity: 0.7 }}>
                          <span>{trend30.dates[0]}</span>
                          <span>{trend30.dates[trend30.dates.length - 1]}</span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="ledger-item">
                    <h3>12-month trend</h3>
                    {trendYear.total === 0 ? (
                      <p className="status-text">No views yet.</p>
                    ) : (
                      <>
                        <TrendChart trend={trendYear} stroke="#9333ea" gradientId="trend-12" />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', opacity: 0.7 }}>
                          <span>{trendYear.labels[0]}</span>
                          <span>{trendYear.labels[trendYear.labels.length - 1]}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>
        </>
      )}

    </main>
  );
}

export default AnalyticsDashboard;

























